import React, { useState, useEffect } from 'react';
import { PricingSection } from '../components/subscription/PricingSection';
import { VoucherActivation } from '../components/subscription/VoucherActivation';

const PricingPage = () => {
  const [activeTab, setActiveTab] = useState('plans');
  const [userId, setUserId] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) {
      try {
        const u = JSON.parse(stored);
        if (u.id) setUserId(u.id);
      } catch (err) {
        console.error('Failed to parse stored user:', err);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubscribeSuccess = (result) => {
    console.log('Subscription successful:', result);
    setMessage({ type: 'success', text: 'Subscription created successfully!' });
  };

  const handleSubscribeError = (error) => {
    console.error('Subscription error:', error);
    setMessage({ type: 'error', text: error });
  };

  const handleLoginRequired = () => {
    // User clicked a plan but isn't logged in
    // The PricingSection will show the login prompt
    console.log('Login required for subscription');
  };

  const handleVoucherSuccess = (result) => {
    console.log('Voucher activated:', result);
    setMessage({ type: 'success', text: 'Voucher activated successfully! Welcome to Nextdose.' });
  };

  const handleVoucherError = (error) => {
    console.error('Voucher error:', error);
    setMessage({ type: 'error', text: error });
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Simple, Transparent{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                Pricing
              </span>
            </h1>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Choose the perfect plan for your family{"'"}s healthcare needs. 
              All plans include our core features with no hidden fees.
            </p>
          </div>

          {/* Tab Selector */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-slate-800 rounded-xl p-1 border border-slate-700">
              <button
                onClick={() => setActiveTab('plans')}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  activeTab === 'plans'
                    ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/30'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Subscription Plans
              </button>
              <button
                onClick={() => setActiveTab('voucher')}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  activeTab === 'voucher'
                    ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/30'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Activate Voucher
              </button>
            </div>
          </div>

          {/* Content */}
          {activeTab === 'plans' ? (
            <PricingSection
              userId={userId}
              onSubscribeSuccess={handleSubscribeSuccess}
              onSubscribeError={handleSubscribeError}
              onLoginRequired={handleLoginRequired}
            />
          ) : (
            <div className="max-w-2xl mx-auto">
              <VoucherActivation
                userId={userId}
                onSuccess={handleVoucherSuccess}
                onError={handleVoucherError}
              />
            </div>
          )}

          {/* FAQ Section */}
          <div className="mt-20">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {[
                {
                  q: 'Can I cancel my subscription anytime?',
                  a: 'Yes, you can cancel your subscription at any time. Your access will continue until the end of your current billing period.',
                },
                {
                  q: 'What payment methods do you accept?',
                  a: 'We accept all major credit/debit cards, UPI, net banking, and wallet payments through Razorpay.',
                },
                {
                  q: 'Can I upgrade my plan later?',
                  a: 'Absolutely! You can upgrade your plan at any time. The price difference will be prorated for your current billing cycle.',
                },
                {
                  q: 'Is my data secure?',
                  a: 'Yes, we take data security very seriously. All data is encrypted and stored in compliance with DPDP Act guidelines.',
                },
                {
                  q: 'Do you offer refunds?',
                  a: 'We offer a 7-day money-back guarantee for all new subscriptions. Contact our support team for assistance.',
                },
              ].map((faq) => (
                <div
                  key={faq.q}
                  className="bg-slate-800 rounded-xl p-6 border border-slate-700"
                >
                  <h3 className="text-lg font-semibold text-white mb-2">{faq.q}</h3>
                  <p className="text-slate-400">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PricingPage;
